import {
  INTERACTION_ACTION_DUMMY_TYPE_TABLE,
  INTERACTION_ACTION_DUMMY_TYPE_UNIQUE_IDENTIFIER,
  INTERACTION_ACTION_PARAMETER_TYPE_CONTROL,
  INTERACTION_ACTION_PARAMETER_TYPE_TABLE,
  isUpdateAction,
} from '../../constants/editor.constants';
import { SS_CONTROL_TYPE } from '../../constants/form-manager.constants';
import { getTableTranslation } from '../../utils/editor.utils';

export function categorizeRules(originalObj) {
  const categorizedObj = {
    update: {
      rules: [],
    },
    interaction: {
      rules: [],
    },
  };

  const updateRules = originalObj.update.rules ?? [];

  updateRules.forEach((rule) => {
    const interactionActions = rule.actions.filter((action) => !isUpdateAction(action.type));
    const updateActions = rule.actions.filter((action) => isUpdateAction(action.type));

    if (interactionActions.length) {
      categorizedObj.interaction.rules.push({
        ...rule,
        type: 'interaction',
        actions: interactionActions,
      });
    }
    if (updateActions.length) {
      categorizedObj.update.rules.push({
        ...rule,
        actions: updateActions,
      });
    }
    if (!rule.actions?.length) {
      categorizedObj.update.rules.push({
        ...rule,
        actions: [],
      });
    }
  });

  return categorizedObj;
}

export const divideUpdateAndInteractionRules = (controlModel) => {
  delete controlModel.interaction;

  const updateAndInteractionRules = categorizeRules(controlModel);

  delete controlModel.update;

  const updatedControl = { ...controlModel, ...updateAndInteractionRules };

  return updatedControl;
};

export const clearRules = (controlProps, originalControlProps) => {
  if (!controlProps?.update?.rules?.length && !originalControlProps?.update?.rules?.length) {
    delete controlProps.update;
  }
  if (
    !controlProps?.interaction?.rules?.length &&
    !originalControlProps?.interaction?.rules?.length
  ) {
    delete controlProps.interaction;
  }
};

export const rulesSortFn = (a, b) => {
  const indexA = a.rowIndex !== undefined ? a.rowIndex : Number.NEGATIVE_INFINITY;
  const indexB = b.rowIndex !== undefined ? b.rowIndex : Number.NEGATIVE_INFINITY;

  if (indexA !== indexB) {
    return indexA - indexB;
  }

  // If rowIndex is the same or undefined for both, maintain their original order
  return 0;
};

export const getControlModelWithAllUpdateRules = (controlModel) => {
  const controlUpdateRules = controlModel.update?.rules ?? [];
  const controlInteractionRules = controlModel.interaction?.rules ?? [];

  const mergedInteractionRuleIds = [];
  const mergeUpdateRules = controlUpdateRules.map((rule) => {
    const interactionRule = controlInteractionRules.find((intRule) => intRule.id === rule.id);
    if (!interactionRule) {
      return rule;
    }
    mergedInteractionRuleIds.push(interactionRule.id);

    return { ...rule, actions: [...rule.actions, ...interactionRule.actions] };
  });

  const rules = [
    ...mergeUpdateRules,
    ...controlInteractionRules.filter((rule) => !mergedInteractionRuleIds.includes(rule.id)),
  ].sort(rulesSortFn);

  return { ...controlModel, update: { rules } };
};

export const getUniqueIdentifierData = (formModel = {}) => {
  const uniqueIdentifiers = Object.values(formModel).filter(
    (obj) =>
      [SS_CONTROL_TYPE.GRID, SS_CONTROL_TYPE.REPEATING_PANEL].includes(obj.controlType) &&
      !obj.entityName?.includes('V_BAH_BASE_ATTACHMENT_GEN')
  );

  const uniqueIdentifiersResult = [];

  uniqueIdentifiers?.forEach((obj) => {
    if (obj.uniqueIdentifier) {
      uniqueIdentifiersResult.push({
        id: obj.uniqueIdentifier,
        nodeId: obj.uniqueIdentifier,
        value: obj.uniqueIdentifier,
        label: obj.uniqueIdentifier,
      });
    }
  });

  return uniqueIdentifiersResult.sort((a, b) => a.label.localeCompare(b.label));
};

export const getTableData = (entityDefinition, translations) =>
  Object.entries(entityDefinition)
    // eslint-disable-next-line no-unused-vars
    .flatMap(([_, keyItems]) =>
      Object.keys(keyItems)
        .filter((item) => item && item !== 'columns' && item !== 'properties')
        .map((item) => {
          const translatedTableName = getTableTranslation({ translations, table: item });
          return {
            id: item,
            nodeId: item,
            value: item,
            label: `${translatedTableName} (${item})`,
            property: keyItems[item],
          };
        })
    )
    .sort((a, b) => a.label.localeCompare(b.label));

export const getType = ({ formModel = {}, value = '' }) => {
  const uniqueIdentifiers = getUniqueIdentifierData(formModel);
  const uniqueIdentifierValues = uniqueIdentifiers.map((obj) => obj.value);

  return uniqueIdentifierValues.includes(value)
    ? INTERACTION_ACTION_DUMMY_TYPE_UNIQUE_IDENTIFIER
    : INTERACTION_ACTION_DUMMY_TYPE_TABLE;
};

export const getDummyParameter = ({ key, targetDetails = {}, translations = {} }) => {
  if (key !== 'parameter') {
    return '';
  }

  return targetDetails.type === INTERACTION_ACTION_DUMMY_TYPE_UNIQUE_IDENTIFIER
    ? targetDetails.value
    : getTableTranslation({ translations, table: targetDetails.value });
};

export const getParameterType = ({ key, targetDetails = {} }) => {
  if (key !== 'parameter') {
    return '';
  }

  return targetDetails.type === INTERACTION_ACTION_DUMMY_TYPE_UNIQUE_IDENTIFIER
    ? INTERACTION_ACTION_PARAMETER_TYPE_CONTROL
    : INTERACTION_ACTION_PARAMETER_TYPE_TABLE;
};
